import {
  ENTITY_DIRECTORY,
  ENTITY_EXPERIMENT,
  ENTITY_NOTE,
  ENTITY_SOP,
  ENTITY_TASK,
} from 'constants/schemas';
import { useDirectoryItemList, useFetchListing, useMyGlobalPermission } from 'hooks/store.hooks';
import { useEffect, useMemo, useState } from 'react';

import { ACTION_VIEW } from 'constants/permission.constants';
import {
  directoryActions,
} from 'store/entity';
import { useDebouncedFn } from '@acheloisbiosoftware/absui.hooks';

const DEFAULT_TYPES = [ENTITY_DIRECTORY, ENTITY_EXPERIMENT, ENTITY_NOTE, ENTITY_SOP, ENTITY_TASK];
const DEFAULT_EXCLUDE_TYPES = [];

const useGlobalSearch = (search, {
  types = DEFAULT_TYPES,
  excludeTypes = DEFAULT_EXCLUDE_TYPES,
  filter = () => true,
  debounce = 500,
} = {}) => {
  const canViewDirectories = useMyGlobalPermission(ENTITY_DIRECTORY, ACTION_VIEW);
  const canViewExperiments = useMyGlobalPermission(ENTITY_EXPERIMENT, ACTION_VIEW);
  const canViewNotes = useMyGlobalPermission(ENTITY_NOTE, ACTION_VIEW);
  const canViewSops = useMyGlobalPermission(ENTITY_SOP, ACTION_VIEW);
  const canViewTasks = useMyGlobalPermission(ENTITY_TASK, ACTION_VIEW);

  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const _setDebouncedSearch = useDebouncedFn(setDebouncedSearch, debounce);
  const debounceLoading = debounce && debouncedSearch !== search;
  const _search = debounce ? debouncedSearch : search;

  useEffect(() => {
    _setDebouncedSearch(search);
  }, [search, _setDebouncedSearch]);

  const viewPermissionMap = useMemo(() => ({
    [ENTITY_DIRECTORY]: canViewDirectories,
    [ENTITY_EXPERIMENT]: canViewExperiments,
    [ENTITY_NOTE]: canViewNotes,
    [ENTITY_SOP]: canViewSops,
    [ENTITY_TASK]: canViewTasks,
  }), [canViewDirectories, canViewExperiments, canViewNotes, canViewSops, canViewTasks]);

  const _types = useMemo(() => types.filter((type) => (
    viewPermissionMap[type] && !excludeTypes.includes(type)
  )), [types, viewPermissionMap, excludeTypes]);

  const fetchOptions = useMemo(() => ({
    params: { search: _search, filters: { content_types: _types }},
    condition: _search && _types.length > 0,
  }), [_search, _types]);
  const { data, loading } = useFetchListing(directoryActions.fetchDirectoryItems, fetchOptions);

  const resultingDirectoryItemIds = useMemo(() => new Set((data || []).flatMap((res) => res?.payload?.result || [])), [data]);

  const listParams = useMemo(() => ({
    prop: { id: 'object_id', type: 'content_type' },
    filter: (directoryItem, state) => resultingDirectoryItemIds.has(directoryItem.id) && _types.includes(directoryItem.content_type) && filter(directoryItem, state),
  }), [resultingDirectoryItemIds, _types, filter]);

  const showResults = Boolean(search);
  const genericIds = useDirectoryItemList(listParams);
  return useMemo(() => ({
    results: showResults ? genericIds : [],
    loading,
    debounceLoading,
  }), [genericIds, loading, showResults, debounceLoading]);
};

export default useGlobalSearch;
